import { AbstractControl } from '@angular/forms';

export interface DriverSignThreshold {
  driverKey: string;
  driverContext: string;
  signKey: string;
  canAutoFlag: boolean;
  description: string;
  hasData: (controls: { [key: string]: AbstractControl }) => boolean;
  evaluate: (controls: { [key: string]: AbstractControl }) => boolean;
}

function hasEyeValue(controls: { [key: string]: AbstractControl }, signKey: string): boolean {
  const rightCtrl = controls['RIGHT_' + signKey];
  const leftCtrl = controls['LEFT_' + signKey];
  const rightVal = rightCtrl && rightCtrl.value;
  const leftVal = leftCtrl && leftCtrl.value;
  return (
    (rightVal !== null && rightVal !== undefined && rightVal !== '') ||
    (leftVal !== null && leftVal !== undefined && leftVal !== '')
  );
}

function getMethodForEye(
  controls: { [key: string]: AbstractControl },
  prefix: string,
  signKey: string
): string {
  const methodCtrl = controls[prefix + signKey + '_METHOD'];
  return methodCtrl && methodCtrl.value ? methodCtrl.value : 'qualitative';
}

function eitherEyeMeets(
  controls: { [key: string]: AbstractControl },
  signKey: string,
  check: (value: any, method: string) => boolean
): boolean {
  const prefixes = ['RIGHT_', 'LEFT_'];
  for (let i = 0; i < prefixes.length; i++) {
    const prefix = prefixes[i];
    const ctrl = controls[prefix + signKey];
    if (ctrl && ctrl.value !== null && ctrl.value !== undefined && ctrl.value !== '') {
      const method = getMethodForEye(controls, prefix, signKey);
      if (check(ctrl.value, method)) {
        return true;
      }
    }
  }
  return false;
}

// --- Tear Film Drivers ---

const lipidLayerThreshold: DriverSignThreshold = {
  driverKey: 'dewsDriverLipid',
  driverContext: 'lipid',
  signKey: 'LIPID_LAYER',
  canAutoFlag: true,
  description: 'DEWS III threshold: Non-amorphous pattern (grade \u2264 3) or < 72nm',
  hasData: controls => hasEyeValue(controls, 'LIPID_LAYER'),
  evaluate: controls => {
    return eitherEyeMeets(controls, 'LIPID_LAYER', (value, method) => {
      if (method === 'quantitative') {
        return typeof value === 'number' && value < 72;
      }
      return value !== 'grade0';
    });
  }
};

const lipidMgeExpressibilityThreshold: DriverSignThreshold = {
  driverKey: 'dewsDriverLipid',
  driverContext: 'lipid',
  signKey: 'MGE_EXPRESSIBILITY',
  canAutoFlag: true,
  description: 'DEWS III threshold: 3-4 glands expressible or less',
  hasData: controls => hasEyeValue(controls, 'MGE_EXPRESSIBILITY'),
  evaluate: controls => {
    return eitherEyeMeets(controls, 'MGE_EXPRESSIBILITY', value => {
      return value !== 'normal';
    });
  }
};

const lipidMgeSecretionThreshold: DriverSignThreshold = {
  driverKey: 'dewsDriverLipid',
  driverContext: 'lipid',
  signKey: 'MGE_SECRETION',
  canAutoFlag: true,
  description: 'DEWS III threshold: Meibum not clear (grade 1+)',
  hasData: controls => hasEyeValue(controls, 'MGE_SECRETION'),
  evaluate: controls => {
    return eitherEyeMeets(controls, 'MGE_SECRETION', value => {
      return value !== 'minimal';
    });
  }
};

const aqueousTmhThreshold: DriverSignThreshold = {
  driverKey: 'dewsDriverAqueous',
  driverContext: 'aqueous',
  signKey: 'TEAR_MENISCUS_HEIGHT',
  canAutoFlag: true,
  description: 'DEWS III threshold: Tear meniscus height \u2264 0.20mm',
  hasData: controls => hasEyeValue(controls, 'TEAR_MENISCUS_HEIGHT'),
  evaluate: controls => {
    return eitherEyeMeets(controls, 'TEAR_MENISCUS_HEIGHT', value => {
      return typeof value === 'number' && value <= 0.2;
    });
  }
};

const aqueousSchirmersThreshold: DriverSignThreshold = {
  driverKey: 'dewsDriverAqueous',
  driverContext: 'aqueous',
  signKey: 'SCHIRMERS_TEST',
  canAutoFlag: true,
  description: 'DEWS III threshold: < 10mm in 1 min',
  hasData: controls => hasEyeValue(controls, 'SCHIRMERS_TEST'),
  evaluate: controls => {
    return eitherEyeMeets(controls, 'SCHIRMERS_TEST', value => {
      return typeof value === 'number' && value < 10;
    });
  }
};

const aqueousPhenolRedThreshold: DriverSignThreshold = {
  driverKey: 'dewsDriverAqueous',
  driverContext: 'aqueous',
  signKey: 'PHENOL_RED',
  canAutoFlag: true,
  description: 'DEWS III threshold: < 10mm in 15s',
  hasData: controls => hasEyeValue(controls, 'PHENOL_RED'),
  evaluate: controls => {
    return eitherEyeMeets(controls, 'PHENOL_RED', value => {
      return typeof value === 'number' && value < 10;
    });
  }
};

const aqueousSmtubeThreshold: DriverSignThreshold = {
  driverKey: 'dewsDriverAqueous',
  driverContext: 'aqueous',
  signKey: 'SMTUBE',
  canAutoFlag: true,
  description: 'DEWS III threshold: \u2264 2.5mm wetting length',
  hasData: controls => hasEyeValue(controls, 'SMTUBE'),
  evaluate: controls => {
    return eitherEyeMeets(controls, 'SMTUBE', value => {
      return typeof value === 'number' && value <= 2.5;
    });
  }
};

const mucinCornealStainingThreshold: DriverSignThreshold = {
  driverKey: 'dewsDriverMucinGlycocalyx',
  driverContext: 'mucinGlycocalyx',
  signKey: 'CORNEAL_STAINING_EXTENT',
  canAutoFlag: true,
  description: 'DEWS III threshold: > 9 conjunctival punctate spots',
  hasData: controls => hasEyeValue(controls, 'CORNEAL_STAINING_EXTENT'),
  evaluate: controls => {
    return eitherEyeMeets(controls, 'CORNEAL_STAINING_EXTENT', (value, method) => {
      if (method === 'quantitative') {
        return value === 'severe';
      }
      return value === 'grade3';
    });
  }
};

// --- Eyelid Anomaly Drivers ---

const blinkPartialFrequencyThreshold: DriverSignThreshold = {
  driverKey: 'dewsDriverBlinkMechanism',
  driverContext: 'blinkMechanism',
  signKey: 'PARTIAL_BLINKING_FREQUENCY',
  canAutoFlag: true,
  description: 'DEWS III threshold: > 40% partial blinks',
  hasData: controls => hasEyeValue(controls, 'PARTIAL_BLINKING_FREQUENCY'),
  evaluate: controls => {
    return eitherEyeMeets(controls, 'PARTIAL_BLINKING_FREQUENCY', value => {
      return typeof value === 'number' && value > 40;
    });
  }
};

const blinkLagophthalmusThreshold: DriverSignThreshold = {
  driverKey: 'dewsDriverBlinkMechanism',
  driverContext: 'blinkMechanism',
  signKey: 'LAGOPHTHALMUS',
  canAutoFlag: true,
  description: 'DEWS III threshold: Lagophthalmos positive / observed',
  hasData: controls => hasEyeValue(controls, 'LAGOPHTHALMUS'),
  evaluate: controls => {
    return eitherEyeMeets(controls, 'LAGOPHTHALMUS', value => {
      return value === 'positive';
    });
  }
};

const lidMarginCollarettesThreshold: DriverSignThreshold = {
  driverKey: 'dewsDriverLidMargin',
  driverContext: 'lidMargin',
  signKey: 'COLLARETTES',
  canAutoFlag: true,
  description: 'DEWS III threshold: Eyelid debris (collarettes) present',
  hasData: controls => hasEyeValue(controls, 'COLLARETTES'),
  evaluate: controls => {
    return eitherEyeMeets(controls, 'COLLARETTES', value => {
      return value !== 'normal';
    });
  }
};

const lidMarginMeibographyUpperThreshold: DriverSignThreshold = {
  driverKey: 'dewsDriverLidMargin',
  driverContext: 'lidMargin',
  signKey: 'MEIBOGRAPHY_UPPER',
  canAutoFlag: true,
  description: 'DEWS III threshold: Gland length < 75% (grade 3+)',
  hasData: controls => hasEyeValue(controls, 'MEIBOGRAPHY_UPPER'),
  evaluate: controls => {
    return eitherEyeMeets(controls, 'MEIBOGRAPHY_UPPER', value => {
      return value === 'grade3' || value === 'grade4';
    });
  }
};

const lidMarginMeibographyLowerThreshold: DriverSignThreshold = {
  driverKey: 'dewsDriverLidMargin',
  driverContext: 'lidMargin',
  signKey: 'MEIBOGRAPHY_LOWER',
  canAutoFlag: true,
  description: 'DEWS III threshold: Gland length < 75% (grade 3+)',
  hasData: controls => hasEyeValue(controls, 'MEIBOGRAPHY_LOWER'),
  evaluate: controls => {
    return eitherEyeMeets(controls, 'MEIBOGRAPHY_LOWER', value => {
      return value === 'grade3' || value === 'grade4';
    });
  }
};

const lidMarginGlandPluggingThreshold: DriverSignThreshold = {
  driverKey: 'dewsDriverLidMargin',
  driverContext: 'lidMargin',
  signKey: 'MG_ORIFICE_CAPPED_GLANDS',
  canAutoFlag: true,
  description: 'DEWS III threshold: Gland plugging observed',
  hasData: controls => hasEyeValue(controls, 'MG_ORIFICE_CAPPED_GLANDS'),
  evaluate: controls => {
    return eitherEyeMeets(controls, 'MG_ORIFICE_CAPPED_GLANDS', value => {
      return typeof value === 'string' && value.trim().length > 0;
    });
  }
};

const lidMarginTelangiectasiaThreshold: DriverSignThreshold = {
  driverKey: 'dewsDriverLidMargin',
  driverContext: 'lidMargin',
  signKey: 'EYELID_TELANGIECTASIA',
  canAutoFlag: true,
  description: 'DEWS III threshold: Telangiectasia observed',
  hasData: controls => hasEyeValue(controls, 'EYELID_TELANGIECTASIA'),
  evaluate: controls => {
    return eitherEyeMeets(controls, 'EYELID_TELANGIECTASIA', value => {
      return value !== 'normal';
    });
  }
};

const lidMarginMgeExpressibilityThreshold: DriverSignThreshold = {
  driverKey: 'dewsDriverLidMargin',
  driverContext: 'lidMargin',
  signKey: 'MGE_EXPRESSIBILITY',
  canAutoFlag: true,
  description: 'DEWS III threshold: 3-4 glands expressible or less',
  hasData: controls => hasEyeValue(controls, 'MGE_EXPRESSIBILITY'),
  evaluate: controls => {
    return eitherEyeMeets(controls, 'MGE_EXPRESSIBILITY', value => {
      return value !== 'normal';
    });
  }
};

const lidMarginMgeSecretionThreshold: DriverSignThreshold = {
  driverKey: 'dewsDriverLidMargin',
  driverContext: 'lidMargin',
  signKey: 'MGE_SECRETION',
  canAutoFlag: true,
  description: 'DEWS III threshold: Meibum not clear (grade 1+)',
  hasData: controls => hasEyeValue(controls, 'MGE_SECRETION'),
  evaluate: controls => {
    return eitherEyeMeets(controls, 'MGE_SECRETION', value => {
      return value !== 'minimal';
    });
  }
};

const lidMarginKeratinizationThreshold: DriverSignThreshold = {
  driverKey: 'dewsDriverLidMargin',
  driverContext: 'lidMargin',
  signKey: 'KERATINIZATION',
  canAutoFlag: true,
  description: 'DEWS III threshold: Keratinization observed',
  hasData: controls => hasEyeValue(controls, 'KERATINIZATION'),
  evaluate: controls => {
    return eitherEyeMeets(controls, 'KERATINIZATION', value => {
      return value === 'observed';
    });
  }
};

// --- Ocular Surface Drivers ---

const anatomicalAlignmentEyeMarkupThreshold: DriverSignThreshold = {
  driverKey: 'dewsDriverAnatomicalAlignment',
  driverContext: 'anatomicalAlignment',
  signKey: 'EYE_MARKUP',
  canAutoFlag: false,
  description: '',
  hasData: () => false,
  evaluate: () => false
};

const neuralDysfunctionCornealSensationThreshold: DriverSignThreshold = {
  driverKey: 'dewsDriverNeuralDysfunction',
  driverContext: 'neuralDysfunction',
  signKey: 'CORNEAL_SENSATION',
  canAutoFlag: true,
  description:
    'DEWS III threshold: Abnormal corneal sensation (hyposensitivity or hypersensitivity)',
  hasData: controls => hasEyeValue(controls, 'CORNEAL_SENSATION'),
  evaluate: controls => {
    return eitherEyeMeets(controls, 'CORNEAL_SENSATION', value => {
      return value !== 'normal';
    });
  }
};

const cellDamageFluoresceinStainingThreshold: DriverSignThreshold = {
  driverKey: 'dewsDriverCellDamage',
  driverContext: 'cellDamageDisruption',
  signKey: 'FLUORESCEIN_STAINING',
  canAutoFlag: true,
  description: 'DEWS III threshold: > 5 corneal fluorescein punctate spots (grade 2+)',
  hasData: controls => hasEyeValue(controls, 'FLUORESCEIN_STAINING'),
  evaluate: controls => {
    return eitherEyeMeets(controls, 'FLUORESCEIN_STAINING', value => {
      return value === 'grade2' || value === 'grade3';
    });
  }
};

const cellDamageCornealStainingThreshold: DriverSignThreshold = {
  driverKey: 'dewsDriverCellDamage',
  driverContext: 'cellDamageDisruption',
  signKey: 'CORNEAL_STAINING_EXTENT',
  canAutoFlag: true,
  description: 'DEWS III threshold: > 5 corneal fluorescein punctate spots',
  hasData: controls => hasEyeValue(controls, 'CORNEAL_STAINING_EXTENT'),
  evaluate: controls => {
    return eitherEyeMeets(controls, 'CORNEAL_STAINING_EXTENT', (value, method) => {
      if (method === 'quantitative') {
        return value !== 'normal';
      }
      return value !== 'grade0';
    });
  }
};

const cellDamageLweThreshold: DriverSignThreshold = {
  driverKey: 'dewsDriverCellDamage',
  driverContext: 'cellDamageDisruption',
  signKey: 'LWE',
  canAutoFlag: true,
  description: 'DEWS III threshold: Lid wiper staining > 2mm length & 25% width',
  hasData: controls => hasEyeValue(controls, 'LWE'),
  evaluate: controls => {
    return eitherEyeMeets(controls, 'LWE', value => {
      return value !== 'noLidWiper' && value !== 'grade0';
    });
  }
};

const mmp9InflammationThreshold: DriverSignThreshold = {
  driverKey: 'dewsDriverInflammation',
  driverContext: 'inflammationOxidativeStress',
  signKey: 'MMP9',
  canAutoFlag: true,
  description: 'DEWS III threshold: ≥ 40ng/mL (Weak Positive or above)',
  hasData: controls => hasEyeValue(controls, 'MMP9'),
  evaluate: controls => {
    return eitherEyeMeets(controls, 'MMP9', value => {
      return value === 'weak' || value === 'positive' || value === 'strong';
    });
  }
};

const lidRednessInflammationThreshold: DriverSignThreshold = {
  driverKey: 'dewsDriverInflammation',
  driverContext: 'inflammationOxidativeStress',
  signKey: 'LID_REDNESS',
  canAutoFlag: true,
  description: 'DEWS III threshold: > grade 2 (Moderate or Severe)',
  hasData: controls => hasEyeValue(controls, 'LID_REDNESS'),
  evaluate: controls => {
    return eitherEyeMeets(controls, 'LID_REDNESS', value => {
      return value === 'moderate' || value === 'severe';
    });
  }
};

const conjunctivalChalasisAnatomicalThreshold: DriverSignThreshold = {
  driverKey: 'dewsDriverAnatomicalAlignment',
  driverContext: 'anatomicalAlignment',
  signKey: 'CONJUNCTIVAL_CHALASIS',
  canAutoFlag: true,
  description: 'DEWS III threshold: > grade 1',
  hasData: controls => hasEyeValue(controls, 'CONJUNCTIVAL_CHALASIS'),
  evaluate: controls => {
    return eitherEyeMeets(controls, 'CONJUNCTIVAL_CHALASIS', value => {
      return value !== 'grade0';
    });
  }
};

const inflammationBulbarRednessThreshold: DriverSignThreshold = {
  driverKey: 'dewsDriverInflammation',
  driverContext: 'inflammationOxidativeStress',
  signKey: 'BULBAR_REDNESS',
  canAutoFlag: true,
  description: 'DEWS III threshold: > 0.95 objective or > grade 2',
  hasData: controls => hasEyeValue(controls, 'BULBAR_REDNESS'),
  evaluate: controls => {
    return eitherEyeMeets(controls, 'BULBAR_REDNESS', (value, method) => {
      if (method === 'quantitative') {
        return typeof value === 'number' && value > 0.95;
      }
      return value === 'grade3' || value === 'grade4';
    });
  }
};

const nibutHomeostasisThreshold: DriverSignThreshold = {
  driverKey: '',
  driverContext: 'tearFilmStability',
  signKey: 'NITBUT',
  canAutoFlag: true,
  description: 'DEWS III threshold: NIBUT < 10 seconds',
  hasData: controls => hasEyeValue(controls, 'NITBUT'),
  evaluate: controls => eitherEyeMeets(controls, 'NITBUT', value => typeof value === 'number' && value < 10)
};

const itbutHomeostasisThreshold: DriverSignThreshold = {
  driverKey: '',
  driverContext: 'tearFilmStability',
  signKey: 'ITBUT',
  canAutoFlag: true,
  description: 'DEWS III threshold: FBUT < 5 seconds',
  hasData: controls => hasEyeValue(controls, 'ITBUT'),
  evaluate: controls => eitherEyeMeets(controls, 'ITBUT', value => typeof value === 'number' && value < 5)
};

const osmolarityHomeostasisThreshold: DriverSignThreshold = {
  driverKey: '',
  driverContext: 'osmolarity',
  signKey: 'OSMOLARITY',
  canAutoFlag: true,
  description: 'DEWS III threshold: ≥ 308 mOsm/kg or inter-eye difference > 8 mOsm/kg',
  hasData: controls => hasEyeValue(controls, 'OSMOLARITY'),
  evaluate: controls => {
    const rightCtrl = controls['RIGHT_OSMOLARITY'];
    const leftCtrl = controls['LEFT_OSMOLARITY'];
    const rightVal = rightCtrl && rightCtrl.value !== null && rightCtrl.value !== undefined && rightCtrl.value !== ''
      ? rightCtrl.value : null;
    const leftVal = leftCtrl && leftCtrl.value !== null && leftCtrl.value !== undefined && leftCtrl.value !== ''
      ? leftCtrl.value : null;
    if (rightVal !== null && rightVal >= 308) { return true; }
    if (leftVal !== null && leftVal >= 308) { return true; }
    if (rightVal !== null && leftVal !== null && Math.abs(rightVal - leftVal) > 8) { return true; }
    return false;
  }
};

export const HOMEOSTASIS_THRESHOLDS: DriverSignThreshold[] = [
  nibutHomeostasisThreshold,
  itbutHomeostasisThreshold,
  osmolarityHomeostasisThreshold
];

export const DRIVER_THRESHOLDS: DriverSignThreshold[] = [
  // Tear Film
  lipidLayerThreshold,
  aqueousTmhThreshold,
  aqueousSchirmersThreshold,
  aqueousPhenolRedThreshold,
  aqueousSmtubeThreshold,
  mucinCornealStainingThreshold,
  // Eyelid Anomaly
  blinkPartialFrequencyThreshold,
  blinkLagophthalmusThreshold,
  lidMarginCollarettesThreshold,
  lidMarginMeibographyUpperThreshold,
  lidMarginMeibographyLowerThreshold,
  lidMarginGlandPluggingThreshold,
  lidMarginTelangiectasiaThreshold,
  lidMarginMgeExpressibilityThreshold,
  lidMarginMgeSecretionThreshold,
  lidMarginKeratinizationThreshold,
  // Ocular Surface
  anatomicalAlignmentEyeMarkupThreshold,
  conjunctivalChalasisAnatomicalThreshold,
  neuralDysfunctionCornealSensationThreshold,
  cellDamageFluoresceinStainingThreshold,
  cellDamageCornealStainingThreshold,
  cellDamageLweThreshold,
  inflammationBulbarRednessThreshold,
  mmp9InflammationThreshold,
  lidRednessInflammationThreshold
];

export function getDriverKeys(): string[] {
  const seen: { [key: string]: boolean } = {};
  const keys: string[] = [];
  DRIVER_THRESHOLDS.forEach(t => {
    if (!seen[t.driverKey]) {
      seen[t.driverKey] = true;
      keys.push(t.driverKey);
    }
  });
  return keys;
}
